import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCustomerInvoicesForShop } from '@/hooks/invoicing';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatInvoiceNumber } from '@/lib/invoicing/formatInvoiceNumber';
import { InvoiceStatusBadge } from '@/components/invoicing/InvoiceStatusBadge';
import { useExposureFlag } from '@/hooks/useFeatureFlags';
import { usePermissions } from '@/hooks/usePermissions';

const InvoicesList = () => {
  const enabled = useExposureFlag('ui_customer_invoices');
  const { canViewCustomerInvoices } = usePermissions();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCustomerInvoicesForShop({ status, search });

  if (!enabled || !canViewCustomerInvoices) {
    return <div className="p-8 text-muted-foreground">Customer invoicing is not enabled.</div>;
  }

  return (
    <div className="container max-w-6xl mx-auto p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Invoices & Estimates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input placeholder="Search by number, customer, phone…"
              value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="estimate">Estimate</SelectItem>
                <SelectItem value="authorized">Authorized</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{formatInvoiceNumber(inv)}</TableCell>
                    <TableCell><InvoiceStatusBadge status={inv.status} /></TableCell>
                    <TableCell>{inv.customer_name ?? '—'}</TableCell>
                    <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right tabular-nums">${Number(inv.grand_total).toFixed(2)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(inv.balance_due) > 0
                        ? <span className="text-destructive">${Number(inv.balance_due).toFixed(2)}</span>
                        : <span className="text-muted-foreground">$0.00</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="ghost">
                        <Link to={`/invoices/${inv.id}`}><ExternalLink className="h-4 w-4" /></Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(data ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No invoices found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoicesList;
